import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { DOCUMENT_PARTIES, type DocumentParty } from './documents';
import { useMeQuery } from './engagement-lifecycle';

/**
 * Document sources (E18-F01 / T-0211): the SharePoint folder an engagement
 * lead connects from the Documents tab, which the sync pass reads every five
 * minutes through Microsoft Graph (ADR-021). Routes per the E18 design note
 * §4, all under `/api/v1/engagements/:engagement_id/document-sources`:
 *
 *   GET    /             → `{ source: DocumentSourceDto | null }`
 *   POST   /             → connect `{ folder_url, source_party }`: the API
 *                          resolves the URL and connects in one step, replacing
 *                          an existing connection. engagement_lead.
 *   GET    /:id/items    → the folder's entries, `?state=skipped|removed|synced`
 *   POST   /:id/sync     → 202; the worker runs a pass as soon as it can take
 *                          the lock. engagement_lead.
 *   DELETE /:id          → disconnect; documents already taken stay. engagement_lead.
 *
 * The API ships alongside this screen (T-0209), so every field the card can
 * do without is optional and an older server still parses.
 */

/** Whose documents the folder holds: the document party (E19 / T-0215). */
export const DOCUMENT_SOURCE_PARTIES = DOCUMENT_PARTIES;
export type DocumentSourceParty = DocumentParty;

export const DocumentSourceDtoSchema = z
  .object({
    id: z.string(),
    /** `sharepoint` */
    kind: z.string(),
    /** `active` | `access_lost` | `disconnected` */
    status: z.string(),
    site_name: z.string().nullable().optional(),
    /** e.g. `Oregon Metro IV&V / Contract documents` */
    display_path: z.string(),
    web_url: z.string(),
    source_party: z.string().optional(),
    last_sync_at: z.string().nullable().optional(),
    next_sync_at: z.string().nullable().optional(),
    /** Set while a pass holds the source (the lock-takeover clock). Not on every server. */
    sync_started_at: z.string().nullable().optional(),
    last_sync_error: z.string().nullable().optional(),
    synced_count: z.number().int().nonnegative().optional(),
    skipped_count: z.number().int().nonnegative().optional(),
    connected_by: z.object({ user_id: z.string(), display_name: z.string() }).nullable().optional(),
    connected_at: z.string().optional(),
  })
  .passthrough();
export type DocumentSourceDto = z.infer<typeof DocumentSourceDtoSchema>;

export const DocumentSourceResponseSchema = z.object({
  source: DocumentSourceDtoSchema.nullable(),
});

/** Connect answers with the DTO; `{ source }`, as the GET answers, is accepted too. */
const ConnectDocumentSourceResultSchema = z.union([
  z.object({ source: DocumentSourceDtoSchema }).transform((r) => r.source),
  DocumentSourceDtoSchema,
]);

export const DOCUMENT_SOURCE_ITEM_STATES = ['synced', 'skipped', 'removed', 'failed'] as const;
export type DocumentSourceItemState = (typeof DOCUMENT_SOURCE_ITEM_STATES)[number];

export const DocumentSourceItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    path: z.string().nullable().optional(),
    /** `synced` | `skipped` | `removed` | `failed` */
    state: z.string(),
    /** Why the file was skipped or failed, in plain words. */
    reason: z.string().nullable().optional(),
    document_id: z.string().nullable().optional(),
    replaced_by_item_id: z.string().nullable().optional(),
    byte_size: z.number().nullable().optional(),
    mime_type: z.string().nullable().optional(),
    modified_at: z.string().nullable().optional(),
    last_seen_at: z.string().nullable().optional(),
  })
  .passthrough();
export type DocumentSourceItem = z.infer<typeof DocumentSourceItemSchema>;

/** `{ items }`, or a bare array from an earlier server revision. */
const DocumentSourceItemsSchema = z.union([
  z.object({ items: z.array(DocumentSourceItemSchema) }),
  z.array(DocumentSourceItemSchema),
]);

export interface ConnectDocumentSourceInput {
  folder_url: string;
  source_party: DocumentSourceParty;
}

function basePath(engagementId: string): string {
  return `/api/v1/engagements/${encodeURIComponent(engagementId)}/document-sources`;
}

export function getDocumentSource(engagementId: string): Promise<DocumentSourceDto | null> {
  return apiClient(basePath(engagementId), DocumentSourceResponseSchema).then((r) => r.source);
}

export function listDocumentSourceItems(
  engagementId: string,
  sourceId: string,
  state: DocumentSourceItemState,
): Promise<DocumentSourceItem[]> {
  return apiClient(
    `${basePath(engagementId)}/${encodeURIComponent(sourceId)}/items`,
    DocumentSourceItemsSchema,
    { query: { state } },
  ).then((v) => (Array.isArray(v) ? v : v.items));
}

export function connectDocumentSource(
  engagementId: string,
  input: ConnectDocumentSourceInput,
  idempotencyKey: string,
): Promise<DocumentSourceDto> {
  return apiClient(
    basePath(engagementId),
    // The wrapped form transforms to the DTO, so the schema's input and output
    // differ; `apiClient` types its schema on the output alone.
    ConnectDocumentSourceResultSchema as unknown as z.ZodType<DocumentSourceDto>,
    { method: 'POST', body: input, idempotencyKey },
  );
}

export function syncDocumentSourceNow(engagementId: string, sourceId: string): Promise<void> {
  return apiClient(`${basePath(engagementId)}/${encodeURIComponent(sourceId)}/sync`, z.unknown(), {
    method: 'POST',
  }).then(() => undefined);
}

export function disconnectDocumentSource(engagementId: string, sourceId: string): Promise<void> {
  return apiClient(`${basePath(engagementId)}/${encodeURIComponent(sourceId)}`, z.unknown(), {
    method: 'DELETE',
  }).then(() => undefined);
}

// ---- Pure state helpers (unit-tested without the DOM) -----------------------

type SourceTiming = Pick<
  DocumentSourceDto,
  'status' | 'last_sync_at' | 'next_sync_at' | 'sync_started_at' | 'last_sync_error'
>;

function isPast(iso: string | null | undefined, now: number): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && t <= now;
}

/** A pass holds the source right now (the API says so). */
export function isDocumentSourceLocked(source: SourceTiming): boolean {
  return Boolean(source.sync_started_at);
}

/**
 * A pass is reading the folder: the lock is held, or — on a server that does
 * not report the lock — a pass is due and the worker has not reported yet.
 */
export function isDocumentSourceSyncRunning(
  source: SourceTiming,
  now: number = Date.now(),
): boolean {
  if (source.sync_started_at !== undefined) return source.sync_started_at !== null;
  return isPast(source.next_sync_at, now);
}

/** The folder has never been read, or its next pass is due. */
export function isDocumentSourceSyncDue(source: SourceTiming, now: number = Date.now()): boolean {
  return !source.last_sync_at || isPast(source.next_sync_at, now);
}

/** How often the card polls while a pass is due or running on an active folder. */
export const DOCUMENT_SOURCE_POLL_BUSY_MS = 30_000;
/** How often otherwise: the pass runs every five minutes. */
export const DOCUMENT_SOURCE_POLL_IDLE_MS = 5 * 60_000;

export function documentSourcePollInterval(
  source: SourceTiming | null | undefined,
  now: number = Date.now(),
): number {
  if (!source || source.status !== 'active') return DOCUMENT_SOURCE_POLL_IDLE_MS;
  return isDocumentSourceSyncDue(source, now) || isDocumentSourceSyncRunning(source, now)
    ? DOCUMENT_SOURCE_POLL_BUSY_MS
    : DOCUMENT_SOURCE_POLL_IDLE_MS;
}

export interface DocumentSourceStatusLine {
  tone: 'quiet' | 'busy' | 'critical';
  message: string;
}

/**
 * The card's status line, in the spec's words. Null when there is nothing to
 * say: the folder was read and the "Synced … · N files" line carries it.
 *
 *   access lost          → "Caliber can no longer read this folder" (critical)
 *   a pass holds the lock → "Reading…"
 *   never read           → "Not yet synced"
 *   a pass is due and the server does not report the lock → "Reading…"
 *   the last pass failed → its error, quietly
 */
export function describeDocumentSource(
  source: SourceTiming,
  now: number = Date.now(),
): DocumentSourceStatusLine | null {
  if (source.status === 'access_lost') {
    return { tone: 'critical', message: 'Caliber can no longer read this folder' };
  }
  if (isDocumentSourceLocked(source)) return { tone: 'busy', message: 'Reading…' };
  if (!source.last_sync_at) return { tone: 'quiet', message: 'Not yet synced' };
  if (isDocumentSourceSyncRunning(source, now)) return { tone: 'busy', message: 'Reading…' };
  if (source.last_sync_error) return { tone: 'quiet', message: source.last_sync_error };
  return null;
}

// ---- TanStack Query hooks --------------------------------------------------

export function documentSourceQueryKey(engagementId: string | undefined) {
  return ['document-source', engagementId] as const;
}

/**
 * The engagement's connected folder. Polls every 30 s while the folder is
 * active and a pass is due or running, otherwise every 5 min (the pass's own
 * cadence), so the card follows a sync without a reload.
 */
export function useDocumentSourceQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: documentSourceQueryKey(engagementId),
    queryFn: () => getDocumentSource(engagementId!),
    enabled: Boolean(engagementId),
    refetchInterval: (q) => documentSourcePollInterval(q.state.data ?? null),
  });
}

/**
 * The folder's entries in one state, for the card's skipped and removed
 * lists. `syncedAt` (the source's `last_sync_at`) is part of the key, so a
 * new pass fetches fresh lists.
 */
export function useDocumentSourceItemsQuery(
  engagementId: string | undefined,
  sourceId: string | undefined,
  state: DocumentSourceItemState,
  opts?: { enabled?: boolean; syncedAt?: string | null },
) {
  return useQuery({
    queryKey: [
      'document-source',
      engagementId,
      'items',
      sourceId,
      state,
      opts?.syncedAt ?? null,
    ] as const,
    queryFn: () => listDocumentSourceItems(engagementId!, sourceId!, state),
    enabled: Boolean(engagementId && sourceId) && (opts?.enabled ?? true),
    staleTime: 30_000,
  });
}

/**
 * Connect a folder. Every call mints its own Idempotency-Key: a second try
 * after a refused URL is a new request, not a replay. The card shows the
 * returned source at once; the documents list is refreshed because a
 * replaced connection may change which documents carry a source.
 */
export function useConnectDocumentSourceMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ConnectDocumentSourceInput) =>
      connectDocumentSource(engagementId!, input, crypto.randomUUID()),
    onSuccess: (source) => {
      qc.setQueryData(documentSourceQueryKey(engagementId), source);
      void qc.invalidateQueries({ queryKey: documentSourceQueryKey(engagementId) });
      void qc.invalidateQueries({ queryKey: ['engagement-documents', engagementId] });
    },
  });
}

/** Ask for a pass now (202). The source is re-read so the card shows it reading. */
export function useSyncNowMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: string) => syncDocumentSourceNow(engagementId!, sourceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: documentSourceQueryKey(engagementId) });
      void qc.invalidateQueries({ queryKey: ['engagement-documents', engagementId] });
    },
  });
}

/** Disconnect. The card returns to not connected; the documents already taken stay. */
export function useDisconnectDocumentSourceMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: string) => disconnectDocumentSource(engagementId!, sourceId),
    onSuccess: () => {
      qc.setQueryData(documentSourceQueryKey(engagementId), null);
      void qc.invalidateQueries({ queryKey: documentSourceQueryKey(engagementId) });
      void qc.invalidateQueries({ queryKey: ['engagement-documents', engagementId] });
    },
  });
}

/**
 * Whether the signed-in user may connect, sync now or disconnect: the API's
 * floor is engagement_lead, which a firm admin clears too. True only once
 * `/me` has said so; a consultant sees the card without the controls, and the
 * write routes refuse on their own.
 */
export function useCanManageDocumentSources(): boolean {
  const role = useMeQuery().data?.role;
  return role === 'engagement_lead' || role === 'firm_admin';
}
